import { useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { rolesRepository, type CreateRoleInput } from '../repository';
import { useRolesStore, type RoleDefinition } from '@/store/rolesStore';
import type { Permission } from '@/lib/config/constants';
import type { ApiError } from '@/types/common';

const ROLES_KEY = ['roles'] as const;

export function useRoles() {
  const setRoles = useRolesStore((s) => s.setRoles);
  const query = useQuery({
    queryKey: ROLES_KEY,
    queryFn: () => rolesRepository.list(),
    staleTime: 5 * 60 * 1000,
  });
  useEffect(() => {
    if (query.data) setRoles(query.data);
  }, [query.data, setRoles]);
  return query;
}

export function useCreateRole() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateRoleInput): Promise<RoleDefinition> => rolesRepository.create(input),
    onSuccess: () => qc.invalidateQueries({ queryKey: ROLES_KEY }),
  });
}

export function useUpdateRolePermissions() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, permissions }: { id: string; permissions: Permission[] }) =>
      rolesRepository.update(id, permissions),
    onMutate: async ({ id, permissions }) => {
      await qc.cancelQueries({ queryKey: ROLES_KEY });
      const prev = qc.getQueryData<RoleDefinition[]>(ROLES_KEY);
      qc.setQueryData<RoleDefinition[]>(ROLES_KEY, (old) =>
        old?.map((r) => (r.id === id ? { ...r, permissions } : r)),
      );
      return { prev };
    },
    onError: (err: unknown, _vars, ctx) => {
      if (ctx?.prev) qc.setQueryData(ROLES_KEY, ctx.prev);
      toast.error((err as ApiError)?.message ?? 'Failed to update permissions');
    },
    onSettled: () => qc.invalidateQueries({ queryKey: ROLES_KEY }),
  });
}

export function useDeleteRole() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string): Promise<void> => rolesRepository.remove(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ROLES_KEY }),
  });
}
